import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import Pagination from '../ui/pagination';
import { ArrowUpDown, Search } from 'lucide-react';

/**
 * Reusable Data Table Component
 * Used by all widget components for table view
 * 
 * @param {Array} columns - Column definitions [{key, label, format, sortable}]
 * @param {Array} data - Array of row objects
 * @param {Function} onRowClick - Click handler for drill-down
 * @param {Object} pagination - {enabled: boolean, itemsPerPage: number}
 * @param {boolean} sortable - Enable column sorting
 * @param {boolean} searchable - Enable search filtering
 * @param {string} emptyMessage - Message when no data
 */
function DataTable({
    columns = [],
    data = [],
    onRowClick,
    pagination = { enabled: true, itemsPerPage: 10 },
    sortable = true,
    searchable = true,
    emptyMessage = 'No data available'
}) {
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchable || !searchTerm) return data;

        const lowerSearchTerm = searchTerm.toLowerCase();
        return data.filter(row =>
            columns.some(col => {
                const value = row[col.key];
                if (value === null || value === undefined) return false;
                return String(value).toLowerCase().includes(lowerSearchTerm);
            })
        );
    }, [data, searchTerm, columns, searchable]);

    // Sort data
    const sortedData = useMemo(() => {
        if (!sortable || !sortColumn) return filteredData;

        return [...filteredData].sort((a, b) => {
            const aVal = a[sortColumn];
            const bVal = b[sortColumn];

            // Handle null/undefined
            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            // Numeric sorting
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
            }

            // String sorting
            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();
            
            if (sortDirection === 'asc') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });
    }, [filteredData, sortColumn, sortDirection, sortable]);

    // Paginate data
    const paginatedData = useMemo(() => {
        if (!pagination.enabled) return sortedData;

        const startIndex = (currentPage - 1) * pagination.itemsPerPage;
        const endIndex = startIndex + pagination.itemsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, pagination]);

    // Handle sort column click
    const handleSort = (columnKey) => {
        if (!sortable) return;

        if (sortColumn === columnKey) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(columnKey);
            setSortDirection('asc');
        }
    };

    // Handle row click
    const handleRowClick = (row, index) => {
        if (onRowClick) {
            onRowClick(row, index);
        }
    };

    // Format cell value
    const formatCellValue = (value, format) => {
        if (value === null || value === undefined) return '-';

        if (format && typeof format === 'function') {
            return format(value);
        }

        // Default formatting for numbers with commas
        if (typeof value === 'number') {
            return value.toLocaleString();
        }

        return value;
    };

    return (
        <div className="w-full space-y-4">
            {/* Search Bar */}
            {searchable && data.length > 0 && (
                <div className="relative w-full md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1); // Reset to first page on search
                        }}
                        className="pl-9"
                    />
                </div>
            )}

            {/* Table */}
            <div className="border rounded-md overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-brand-light-1">
                                {columns.map((column) => (
                                    <TableHead
                                        key={column.key}
                                        className={`text-brand-dark font-semibold ${
                                            sortable && column.sortable !== false ? 'cursor-pointer hover:bg-brand-light-2' : ''
                                        }`}
                                        onClick={() => column.sortable !== false && handleSort(column.key)}
                                    >
                                        <div className="flex items-center gap-2">
                                            {column.label}
                                            {sortable && column.sortable !== false && (
                                                <ArrowUpDown className="h-4 w-4 text-brand-dark/50" />
                                            )}
                                            {sortColumn === column.key && (
                                                <span className="text-brand-accent">
                                                    {sortDirection === 'asc' ? '↑' : '↓'}
                                                </span>
                                            )}
                                        </div>
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedData.length > 0 ? (
                                paginatedData.map((row, rowIndex) => (
                                    <TableRow
                                        key={rowIndex}
                                        className={onRowClick ? 'cursor-pointer hover:bg-brand-light-1' : ''}
                                        onClick={() => handleRowClick(row, rowIndex)}
                                    >
                                        {columns.map((column) => (
                                            <TableCell key={column.key} className="text-brand-dark">
                                                {formatCellValue(row[column.key], column.format)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="text-center py-8 text-brand-dark/70"
                                    >
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Pagination */}
            {pagination.enabled && sortedData.length > pagination.itemsPerPage && (
                <div className="flex justify-center">
                    <Pagination
                        items={sortedData}
                        onChangePage={setCurrentPage}
                        pageSize={pagination.itemsPerPage}
                        showInfo={true}
                    />
                </div>
            )}
        </div>
    );
}

export default DataTable;
